import { httpsCallable } from "firebase/functions";
import { collection, limit, onSnapshot, query, where } from "firebase/firestore";
import { functions, studioDb } from "../firebase";

const CAMPAIGN_TYPES = {
  COUPON: "coupon",
  RAFFLE: "raffle",
};

export const DEFAULT_CLAIMS_REFRESH_MS = 5000;

function normalizeCampaignType(value = "") {
  return String(value || "").trim().toLowerCase() === CAMPAIGN_TYPES.COUPON
    ? CAMPAIGN_TYPES.COUPON
    : CAMPAIGN_TYPES.RAFFLE;
}

function toTimeMs(value) {
  if (!value) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value);
  const timeMs = parsed.getTime();
  return Number.isFinite(timeMs) ? timeMs : 0;
}

function normalizeStatus(value = "") {
  return String(value || "issued").trim().toLowerCase();
}

export function getClaimTicketPath(claim = {}) {
  const token = String(claim?.ticketToken || claim?.ticket?.ticketToken || "").trim();
  if (!token) return "";
  const type = normalizeCampaignType(claim?.campaignType || claim?.raffle?.campaignType);
  return `/${type === CAMPAIGN_TYPES.COUPON ? "coupons" : "raffles"}/t/${encodeURIComponent(token)}`;
}

export function isAlreadyClaimedResult(result = {}) {
  const value = result && typeof result === "object" ? result : {};
  const status = String(value?.claimStatus || value?.status || value?.code || "").trim().toLowerCase();
  return (
    value.alreadyClaimed === true ||
    value.reused === true ||
    value.duplicate === true ||
    status === "already_claimed" ||
    status === "already-claimed" ||
    status === "already_exists" ||
    status === "already-exists"
  );
}

export function getClaimAlreadyClaimedMessage(claim = {}) {
  const type = normalizeCampaignType(claim?.campaignType || claim?.raffle?.campaignType);
  return type === CAMPAIGN_TYPES.COUPON
    ? "You have already claimed this coupon."
    : "You have already claimed this ticket.";
}

function getAlreadyClaimedDetailsFromError(error = {}) {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  const isAlreadyClaimed =
    code.includes("already-exists") ||
    code.includes("already_exists") ||
    code.includes("already-claimed") ||
    code.includes("already_claimed") ||
    message.includes("already claimed");

  if (!isAlreadyClaimed) return null;
  const details = error?.details && typeof error.details === "object" ? error.details : {};
  return {
    ...details,
    alreadyClaimed: true,
    reused: true,
  };
}

export function getCampaignClaimPath(campaign = {}) {
  const campaignId = String(campaign?.campaignId || campaign?.id || campaign?.raffleId || "").trim();
  if (!campaignId) return "";
  const type = normalizeCampaignType(campaign?.campaignType || campaign?.raffle?.campaignType);
  return `/${type === CAMPAIGN_TYPES.COUPON ? "coupons" : "raffles"}/c/${encodeURIComponent(campaignId)}`;
}

export function getClaimDisplayState(claim = {}, nowMs = Date.now()) {
  const status = normalizeStatus(claim?.ticketStatus || claim?.ticket?.ticketStatus);
  const redeemedAtMs = Number(claim?.redeemedAtMs || claim?.ticket?.redeemedAtMs || 0);
  const claimEndMs = toTimeMs(claim?.claimEndAt || claim?.raffle?.claimEndAt || claim?.raffle?.endAt);
  const isRedeemed = redeemedAtMs > 0 || status === "redeemed";
  const isExpired = !isRedeemed && claimEndMs > 0 && nowMs > claimEndMs;
  const isCoupon = normalizeCampaignType(claim?.campaignType || claim?.raffle?.campaignType) === CAMPAIGN_TYPES.COUPON;

  if (isRedeemed) {
    return { label: "Redeemed", tone: "success", isUsable: false, isRedeemed: true, isExpired: false };
  }
  if (isExpired) {
    return { label: "Expired", tone: "warning", isUsable: false, isRedeemed: false, isExpired: true };
  }
  if (status === "pending_draw") {
    return { label: "Pending Draw", tone: "info", isUsable: false, isRedeemed: false, isExpired: false };
  }
  if (status === "non_winner") {
    return { label: "Not this time", tone: "warning", isUsable: false, isRedeemed: false, isExpired: false };
  }
  if (status === "void") {
    return { label: "Void", tone: "default", isUsable: false, isRedeemed: false, isExpired: false };
  }

  return {
    label: isCoupon ? "Valid" : "Ready",
    tone: "primary",
    isUsable: true,
    isRedeemed: false,
    isExpired: false,
  };
}

export function getClaimResultPresentation(claim = {}, nowMs = Date.now()) {
  const type = normalizeCampaignType(claim?.campaignType || claim?.raffle?.campaignType);
  const state = getClaimDisplayState(claim, nowMs);
  const prizeTitle = String(claim?.prizeTitle || claim?.ticket?.prizeSnapshot?.title || "").trim();

  if (type === CAMPAIGN_TYPES.COUPON) {
    if (state.isRedeemed) {
      return {
        eyebrow: "Redeemed",
        title: "Offer redeemed",
        body: "This coupon has already been used.",
        tone: "success",
        icon: "check",
        actionLabel: "View in My Claims",
        actionPath: "/claims",
      };
    }
    if (state.isExpired) {
      return {
        eyebrow: "Expired",
        title: "Offer expired",
        body: "This coupon is no longer available to redeem.",
        tone: "warning",
        icon: "dot",
        actionLabel: "Browse more offers",
        actionPath: "/",
      };
    }
    return {
      eyebrow: "Ready to use",
      title: "Your coupon is ready",
      body: "Show this QR code to staff when you are ready to redeem.",
      tone: "success",
      icon: "check",
      actionLabel: "Scan to redeem",
    };
  }

  if (state.isRedeemed) {
    return {
      eyebrow: "Redeemed",
      title: "Prize redeemed",
      body: "This raffle prize has already been fulfilled.",
      tone: "success",
      icon: "check",
      actionLabel: "View in My Claims",
      actionPath: "/claims",
    };
  }
  if (state.isExpired) {
    return {
      eyebrow: "Expired",
      title: "Entry expired",
      body: "This raffle entry is no longer active.",
      tone: "warning",
      icon: "dot",
      actionLabel: "Browse more offers",
      actionPath: "/",
    };
  }
  if (state.label === "Pending Draw") {
    return {
      eyebrow: "Pending draw",
      title: "You are entered",
      body: "Check back here after the drawing. This pass will update automatically.",
      tone: "info",
      icon: "pending",
      actionLabel: "Saved in My Claims",
      actionPath: "/claims",
    };
  }
  if (state.label === "Not this time") {
    return {
      eyebrow: "Drawing complete",
      title: "Not this time",
      body: "This entry was not selected. Thanks for playing, and check out more chances to win nearby.",
      tone: "warning",
      icon: "dot",
      actionLabel: "Browse more offers",
      actionPath: "/",
    };
  }
  if (state.label === "Void") {
    return {
      eyebrow: "Void",
      title: "Entry unavailable",
      body: "This raffle entry can no longer be used.",
      tone: "default",
      icon: "dot",
      actionLabel: "Browse more offers",
      actionPath: "/",
    };
  }

  return {
    eyebrow: "You won",
    title: "You won this raffle",
    body: prizeTitle
      ? `Your prize is ${prizeTitle}. Show this QR code to staff to redeem.`
      : "Show this QR code to staff to redeem your prize.",
    tone: "success",
    icon: "check",
    actionLabel: "Scan to redeem",
  };
}

export function sortClaimsForDisplay(rows = [], nowMs = Date.now()) {
  return [...rows].sort((a, b) => {
    const aState = getClaimDisplayState(a, nowMs);
    const bState = getClaimDisplayState(b, nowMs);
    const rank = (state) => {
      if (state.isUsable) return 0;
      if (state.isExpired) return 1;
      if (state.isRedeemed) return 2;
      return 1;
    };
    const rankDiff = rank(aState) - rank(bState);
    if (rankDiff) return rankDiff;
    return Number(b?.claimedAtMs || b?.ticket?.claimedAtMs || 0) - Number(a?.claimedAtMs || a?.ticket?.claimedAtMs || 0);
  });
}

export function getActiveClaimsCount(rows = []) {
  return rows.filter((row) => getClaimDisplayState(row).isUsable).length;
}

function claimCampaignIds(claim = {}) {
  return [
    claim?.campaignId,
    claim?.raffleId,
    claim?.raffle?.id,
    claim?.raffle?.campaignId,
    claim?.ticket?.campaignId,
    claim?.ticket?.raffleId,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
}

export function findClaimForCampaign(claims = [], campaign = {}) {
  const campaignId = String(campaign?.campaignId || campaign?.id || campaign?.raffleId || "").trim();
  if (!campaignId) return null;
  const campaignType = normalizeCampaignType(campaign?.campaignType || campaign?.raffle?.campaignType);
  return (
    (Array.isArray(claims) ? claims : []).find((claim) => {
      const claimType = normalizeCampaignType(claim?.campaignType || claim?.raffle?.campaignType);
      return claimType === campaignType && claimCampaignIds(claim).includes(campaignId);
    }) || null
  );
}

export async function listMyRaffleClaims() {
  const result = await httpsCallable(functions, "listMyRaffleClaims")({
    origin: typeof window !== "undefined" ? window.location.origin : "",
  });
  const claims = Array.isArray(result?.data?.claims) ? result.data.claims : [];
  return sortClaimsForDisplay(claims);
}

export async function getPublicRaffleById({ campaignId, raffleId, campaignType } = {}) {
  const result = await httpsCallable(functions, "getPublicRaffleById")({
    raffleId: String(raffleId || campaignId || "").trim(),
    campaignType: normalizeCampaignType(campaignType),
    origin: typeof window !== "undefined" ? window.location.origin : "",
  });
  return result?.data || null;
}

export async function createRaffleTicket({
  raffleId,
  campaignType,
  claimSource,
  referralCampaignId,
} = {}) {
  try {
    const result = await httpsCallable(functions, "createRaffleTicket")({
      raffleId: String(raffleId || "").trim(),
      campaignType: normalizeCampaignType(campaignType),
      claimSource: claimSource || "discover_claim_page",
      // Capsule that drove this claim, when the user arrived via a capsule CTA
      // (standard §3.5 4b). The server attributes the `claim` back to it.
      referralCampaignId: String(referralCampaignId || "").trim() || undefined,
      origin: typeof window !== "undefined" ? window.location.origin : "",
    });
    const data = result?.data || null;
    return isAlreadyClaimedResult(data) ? { ...data, alreadyClaimed: true } : data;
  } catch (error) {
    const alreadyClaimed = getAlreadyClaimedDetailsFromError(error);
    if (alreadyClaimed) {
      return {
        ...alreadyClaimed,
        raffle: {
          ...(alreadyClaimed.raffle || {}),
          campaignType: normalizeCampaignType(alreadyClaimed?.raffle?.campaignType || campaignType),
        },
      };
    }
    throw error;
  }
}

export async function getRaffleTicketView(ticketToken) {
  const result = await httpsCallable(functions, "getRaffleTicketView")({
    ticketToken: String(ticketToken || "").trim(),
    origin: typeof window !== "undefined" ? window.location.origin : "",
  });
  return result?.data || null;
}

function normalizeUid(value = "") {
  return String(value || "").trim();
}

function createTicketSnapshotUnsubscribe({ ticketToken, userUid, refresh, onError }) {
  const normalizedToken = String(ticketToken || "").trim();
  const normalizedUid = normalizeUid(userUid);
  if (!normalizedToken || !normalizedUid || !studioDb) return null;

  try {
    const ticketsQuery = query(
      collection(studioDb, "raffleTickets"),
      where("ticketToken", "==", normalizedToken),
      where("userUid", "==", normalizedUid),
      limit(1)
    );
    return onSnapshot(
      ticketsQuery,
      () => refresh({ force: true }),
      (error) => {
        if (typeof onError === "function") onError(error);
      }
    );
  } catch (error) {
    if (typeof onError === "function") onError(error);
    return null;
  }
}

function createClaimsSnapshotUnsubscribe({ userUid, refresh, onError }) {
  const normalizedUid = normalizeUid(userUid);
  if (!normalizedUid || !studioDb) return null;

  try {
    const ticketsQuery = query(
      collection(studioDb, "raffleTickets"),
      where("userUid", "==", normalizedUid)
    );
    return onSnapshot(
      ticketsQuery,
      () => refresh({ force: true }),
      (error) => {
        if (typeof onError === "function") onError(error);
      }
    );
  } catch (error) {
    if (typeof onError === "function") onError(error);
    return null;
  }
}

function createLiveRefresh({ load, onNext, onError, intervalMs = DEFAULT_CLAIMS_REFRESH_MS, setupSnapshot }) {
  let active = true;
  let inFlight = false;
  let rerunAfterFlight = false;
  let timerId = null;
  let snapshotUnsubscribe = null;
  const safeIntervalMs = Math.max(1000, Number(intervalMs) || DEFAULT_CLAIMS_REFRESH_MS);

  const refresh = async ({ force = false } = {}) => {
    if (!active) return;
    if (inFlight) {
      rerunAfterFlight = force || rerunAfterFlight;
      return;
    }
    inFlight = true;
    try {
      const nextValue = await load();
      if (active && typeof onNext === "function") onNext(nextValue);
    } catch (error) {
      if (active && typeof onError === "function") onError(error);
    } finally {
      inFlight = false;
      if (active && rerunAfterFlight) {
        rerunAfterFlight = false;
        refresh();
      }
    }
  };

  const refreshWhenVisible = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    refresh();
  };

  refresh();

  if (typeof window !== "undefined") {
    timerId = window.setInterval(refreshWhenVisible, safeIntervalMs);
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("inssa:claims-updated", refreshWhenVisible);
  }
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", refreshWhenVisible);
  }
  if (typeof setupSnapshot === "function") {
    snapshotUnsubscribe = setupSnapshot({ refresh, onError });
  }

  return () => {
    active = false;
    if (typeof snapshotUnsubscribe === "function") snapshotUnsubscribe();
    if (timerId && typeof window !== "undefined") window.clearInterval(timerId);
    if (typeof window !== "undefined") {
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("inssa:claims-updated", refreshWhenVisible);
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    }
  };
}

export function subscribeRaffleTicketView(ticketToken, options = {}) {
  const normalizedToken = String(ticketToken || "").trim();
  return createLiveRefresh({
    ...options,
    load: () => getRaffleTicketView(normalizedToken),
    setupSnapshot: ({ refresh, onError }) =>
      createTicketSnapshotUnsubscribe({
        ticketToken: normalizedToken,
        userUid: options.userUid,
        refresh,
        onError,
      }),
  });
}

export function subscribeMyRaffleClaims(options = {}) {
  return createLiveRefresh({
    ...options,
    load: () => listMyRaffleClaims(),
    setupSnapshot: ({ refresh, onError }) =>
      createClaimsSnapshotUnsubscribe({
        userUid: options.userUid,
        refresh,
        onError,
      }),
  });
}
